import type { Composer } from 'vue-i18n';
import type { TranslationPlaceholder } from '#layers/i18n/types';
import { isTranslationPlaceholder } from '#layers/i18n/utils/is-translation-placeholder';

const PARAMETER_REGEX = /\$t:([\w\-.]+)\((.*?)\)/;

/**
 * Translates a `$t:`-prefixed placeholder using the provided i18n instance.
 * A string with parameters can look like `$t:translation.key({"key1": 1})`.
 *
 * The `i18n` instance MUST be captured during component setup (or passed in
 * by the caller). Inside Vue components, prefer `useTranslationUtils()` which
 * binds an i18n instance at setup time. Calling `useI18n()` here would crash
 * when invoked from a non-setup context (event handlers, async callbacks).
 */
export function translateString<S extends TranslationPlaceholder | undefined>(s: S, i18n: Composer): S {
	if (isTranslationPlaceholder(s)) {
		const match = s.match(PARAMETER_REGEX);

		if (match) {
			const key = match[1];
			const parameters = match[2];

			// Admin-authored strings (e.g. a provider `displayName`) reach this on
			// the public login page; malformed params like `$t:foo({bad)` would
			// otherwise throw. Fall back to the raw string on parse failure.
			let parsedParameters: unknown;

			try {
				parsedParameters = JSON.parse(parameters!);
			}
			catch {
				return s;
			}

			return i18n.te(key!) ? i18n.t(key!, parsedParameters as Record<string, unknown>) as S : s;
		}

		const key = s.slice(3);
		return (i18n.te(key) ? i18n.t(key) : s) as S;
	}

	return s;
}
